import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, UserPlus, Check, XIcon, Search, Users, Trash2, Circle, Clock, MinusCircle, EyeOff, Video, MoreVertical, UserCheck, Navigation, Star, StarOff, Ban } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, WS_URL, AuthContext } from "../App";
import UserActionMenu from "./UserActionMenu";

// Format a "last seen" timestamp as a friendly relative time.
const formatLastSeen = (iso) => {
  if (!iso) return null;
  try {
    const dt = new Date(iso);
    const diffMs = Date.now() - dt.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days}d ago`;
    return dt.toLocaleDateString();
  } catch {
    return null;
  }
};

// Status icon component
const StatusIcon = ({ status, isOnline, size = "w-2 h-2" }) => {
  if (!isOnline) {
    return <Circle className={`${size} text-gray-400 fill-gray-400`} />;
  }
  
  switch (status) {
    case "Away":
      return <Clock className={`${size} text-yellow-500`} />;
    case "Busy":
      return <MinusCircle className={`${size} text-red-500`} />;
    case "Invisible":
      return <EyeOff className={`${size} text-gray-500`} />;
    case "Available":
    default:
      return <Circle className={`${size} text-green-500 fill-green-500`} />;
  }
};

const FriendsPanel = ({ onClose, onStartPM, onRequestWebcam, onViewProfile, draggable = false, fullWidth = false }) => {
  const navigate = useNavigate();
  const { token, user: currentUser } = useContext(AuthContext);
  const [isDesktopFP, setIsDesktopFP] = useState(typeof window !== "undefined" ? window.innerWidth >= 768 : true);
  const [fpPos, setFpPos] = useState({ x: 120, y: 90 });
  const fpDragRef = useRef(false);
  const fpOffRef = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const onR = () => setIsDesktopFP(window.innerWidth >= 768);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  const canDragFp = draggable && isDesktopFP;
  const startFpDrag = (e) => {
    if (!canDragFp || e.target.closest(".close-button-visible")) return;
    fpDragRef.current = true;
    fpOffRef.current = { x: e.clientX - fpPos.x, y: e.clientY - fpPos.y };
    const move = (ev) => {
      if (!fpDragRef.current) return;
      const nx = Math.max(0, Math.min(ev.clientX - fpOffRef.current.x, window.innerWidth - 256));
      const ny = Math.max(0, Math.min(ev.clientY - fpOffRef.current.y, window.innerHeight - 40));
      setFpPos({ x: nx, y: ny });
    };
    const up = () => {
      fpDragRef.current = false;
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [activeTab, setActiveTab] = useState("friends");
  const [loading, setLoading] = useState(true);
  const [menuUser, setMenuUser] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [followingUser, setFollowingUser] = useState(null); // User being followed
  const [showFollowMenu, setShowFollowMenu] = useState(null);
  const [filterMode, setFilterMode] = useState("all"); // all | online | favorites
  const [blockedIds, setBlockedIds] = useState(new Set());
  const lastFollowedRoomRef = useRef(null); // last room we auto-navigated to while following

  const fetchFriends = async () => {
    try {
      const [friendsRes, requestsRes, blockedRes] = await Promise.all([
        axios.get(`${API}/friends`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/friends/requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/blocked`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { blocked_users: [] } }))
      ]);
      setFriends(friendsRes.data);
      setFriendRequests(requestsRes.data);
      setBlockedIds(new Set((blockedRes.data?.blocked_users || []).map(b => b.user_id)));
      
      // If we're following someone, auto-join when they move to a new room
      if (followingUser) {
        const followedFriend = friendsRes.data.find(f => f.user_id === followingUser.user_id);
        if (followedFriend && followedFriend.is_online && followedFriend.current_room
            && followedFriend.current_room !== lastFollowedRoomRef.current) {
          lastFollowedRoomRef.current = followedFriend.current_room;
          toast.info(`Following ${followedFriend.username} to ${followedFriend.current_room.replace(/-/g, ' ')}`);
          navigate(`/room/${followedFriend.current_room}`);
        }
      }
    } catch (error) {
      console.error("Failed to fetch friends:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFriends();
    
    // Refresh friends list every 5 seconds to catch presence and room updates
    const interval = setInterval(fetchFriends, 5000);
    return () => clearInterval(interval);
  }, [token, followingUser]);

  // Follow a user - go to their room now and keep auto-following as they move
  const handleFollow = useCallback((friend) => {
    setFollowingUser(friend);
    if (friend.current_room) {
      lastFollowedRoomRef.current = friend.current_room;
      toast.success(`Now following ${friend.username}. Joining their room...`);
      navigate(`/room/${friend.current_room}`);
    } else {
      lastFollowedRoomRef.current = null;
      toast.info(`Now following ${friend.username}. You'll join automatically when they enter a room.`);
    }
    setShowFollowMenu(null);
  }, [navigate]);

  // Stop following
  const handleStopFollow = useCallback(() => {
    if (followingUser) {
      toast.info(`Stopped following ${followingUser.username}`);
    }
    setFollowingUser(null);
    lastFollowedRoomRef.current = null;
  }, [followingUser]);

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await axios.get(`${API}/users/search?q=${searchQuery}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(searchUsers, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const sendFriendRequest = async (userId) => {
    try {
      await axios.post(`${API}/friends/request`, 
        { friend_user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Friend request sent!");
      setSearchResults(prev => prev.filter(u => u.user_id !== userId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request");
    }
  };

  const acceptRequest = async (userId) => {
    try {
      await axios.post(`${API}/friends/accept`,
        { friend_user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Friend request accepted!");
      fetchFriends();
    } catch (error) {
      toast.error("Failed to accept request");
    }
  };

  const rejectRequest = async (userId) => {
    try {
      await axios.post(`${API}/friends/reject`,
        { friend_user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFriendRequests(prev => prev.filter(r => r.user_id !== userId));
    } catch (error) {
      toast.error("Failed to reject request");
    }
  };

  const removeFriend = async (friendId) => {
    // Stop following if we remove the person we're following
    if (followingUser?.user_id === friendId) {
      setFollowingUser(null);
    }
    try {
      await axios.delete(`${API}/friends/${friendId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Friend removed");
      setFriends(prev => prev.filter(f => f.user_id !== friendId));
    } catch (error) {
      toast.error("Failed to remove friend");
    }
  };

  // Toggle favorite
  const toggleFavorite = async (friendId) => {
    try {
      await axios.post(`${API}/friends/${friendId}/favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchFriends();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle favorite");
    }
  };

  // Unblock helper
  const unblockUser = async (userId, username) => {
    try {
      await axios.post(`${API}/users/unblock/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Unblocked ${username}`);
      fetchFriends();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unblock");
    }
  };

  // Filtered list based on current filterMode
  const visibleFriends = friends.filter(f => {
    if (filterMode === "online") return f.is_online;
    if (filterMode === "favorites") return f.is_favorite;
    return true;
  });

  return (
    <div
      className={`${fullWidth ? 'w-full' : 'w-64'} retro-window`}
      data-testid="friends-panel"
      style={canDragFp ? { position: "fixed", left: fpPos.x, top: fpPos.y, zIndex: 9200 } : undefined}
    >
      <div
        className={`retro-title-bar ${canDragFp ? "cursor-move select-none" : ""}`}
        onMouseDown={startFpDrag}
      >
        <span className="flex items-center gap-1">
          <Users className="w-3 h-3" />
          Friends List ({friends.length})
        </span>
        <button 
          className="close-button-visible"
          onClick={onClose}
          data-testid="close-friends-btn"
          title="Close"
        >
          ✕
        </button>
      </div>

      <div className="p-2 bg-[#C0C0C0]">
        {/* Tabs */}
        <div className="flex gap-1 mb-2">
          <button 
            className={`retro-button flex-1 text-xs py-1 ${activeTab === 'friends' ? 'border-t-[#404040] border-l-[#404040] border-b-[#FFFFFF] border-r-[#FFFFFF]' : ''}`}
            onClick={() => setActiveTab("friends")}
          >
            Friends
          </button>
          <button 
            className={`retro-button flex-1 text-xs py-1 ${activeTab === 'requests' ? 'border-t-[#404040] border-l-[#404040] border-b-[#FFFFFF] border-r-[#FFFFFF]' : ''}`}
            onClick={() => setActiveTab("requests")}
          >
            Requests {friendRequests.length > 0 && `(${friendRequests.length})`}
          </button>
          <button 
            className={`retro-button flex-1 text-xs py-1 ${activeTab === 'search' ? 'border-t-[#404040] border-l-[#404040] border-b-[#FFFFFF] border-r-[#FFFFFF]' : ''}`}
            onClick={() => setActiveTab("search")}
          >
            Add
          </button>
        </div>

        {/* Content */}
        <div className="retro-inset h-64 overflow-y-auto retro-scrollbar">
          {/* Following indicator */}
          {followingUser && (
            <div className="p-2 bg-green-100 border-b border-green-300 flex items-center justify-between">
              <span className="font-arial text-xs text-green-700">
                <Navigation className="w-3 h-3 inline mr-1" />
                Following <strong>{followingUser.username}</strong>
              </span>
              <button 
                className="retro-button text-[10px] px-2 py-0.5 bg-red-100"
                onClick={handleStopFollow}
              >
                Stop
              </button>
            </div>
          )}
          
          {activeTab === "friends" && (
            <div className="p-1">
              {/* Filter buttons */}
              <div className="flex gap-1 mb-2 px-1" data-testid="friends-filters">
                <button
                  className={`retro-button text-[10px] flex-1 py-0.5 ${filterMode === 'all' ? 'border-t-[#404040] border-l-[#404040] border-b-[#FFFFFF] border-r-[#FFFFFF]' : ''}`}
                  onClick={() => setFilterMode("all")}
                  data-testid="filter-all"
                >All</button>
                <button
                  className={`retro-button text-[10px] flex-1 py-0.5 ${filterMode === 'online' ? 'border-t-[#404040] border-l-[#404040] border-b-[#FFFFFF] border-r-[#FFFFFF]' : ''}`}
                  onClick={() => setFilterMode("online")}
                  data-testid="filter-online"
                >Online</button>
                <button
                  className={`retro-button text-[10px] flex-1 py-0.5 ${filterMode === 'favorites' ? 'border-t-[#404040] border-l-[#404040] border-b-[#FFFFFF] border-r-[#FFFFFF]' : ''}`}
                  onClick={() => setFilterMode("favorites")}
                  data-testid="filter-favorites"
                >★ Favs</button>
              </div>
              {loading ? (
                <p className="text-xs text-center text-[#808080] p-4">Loading...</p>
              ) : visibleFriends.length === 0 ? (
                <p className="text-xs text-center text-[#808080] p-4">
                  {filterMode === "online" ? "No online friends" : filterMode === "favorites" ? "No favorites yet — click the ★ next to a friend's name" : "No friends yet"}
                </p>
              ) : (
                visibleFriends.map((friend) => {
                  const isBlocked = blockedIds.has(friend.user_id);
                  return (
                  <div 
                    key={friend.user_id} 
                    className={`flex items-center justify-between p-2 hover:bg-[#E0E0E0] border-b border-[#D0D0D0] cursor-pointer ${followingUser?.user_id === friend.user_id ? 'bg-green-50' : ''}`}
                    data-testid={`friend-${friend.user_id}`}
                    onDoubleClick={() => {
                      if (isBlocked) {
                        if (window.confirm(`Unblock ${friend.username}?`)) unblockUser(friend.user_id, friend.username);
                      } else {
                        onStartPM?.(friend);
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      if (isBlocked) return;
                      setMenuPosition({ x: Math.min(e.clientX, window.innerWidth - 220), y: Math.min(e.clientY, window.innerHeight - 280) });
                      setMenuUser(friend);
                    }}
                    title={isBlocked ? "Blocked — double-click to unblock" : "Double-click to message · right-click for options"}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <StatusIcon 
                        status={friend.status_message} 
                        isOnline={friend.is_online} 
                        size="w-3 h-3"
                      />
                      <button
                        className={`flex-shrink-0 ${friend.is_favorite ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-400'}`}
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(friend.user_id); }}
                        title={friend.is_favorite ? "Remove favorite" : "Mark as favorite"}
                        data-testid={`fav-toggle-${friend.user_id}`}
                      >
                        {friend.is_favorite ? <Star className="w-3 h-3 fill-yellow-400" /> : <StarOff className="w-3 h-3" />}
                      </button>
                      <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-1">
                          <span 
                            className={`font-arial text-xs font-bold truncate ${isBlocked ? 'relative text-gray-500' : ''}`}
                            style={isBlocked ? { textDecoration: 'line-through', textDecorationColor: '#dc2626', textDecorationThickness: '1.5px' } : {}}
                          >
                            {friend.username}
                          </span>
                          {isBlocked && <Ban className="w-3 h-3 text-red-500 flex-shrink-0" title="Blocked" />}
                          {friend.webcam_on && (
                            <Video className="w-3 h-3 text-green-500 flex-shrink-0" title="Webcam On" />
                          )}
                          {followingUser?.user_id === friend.user_id && (
                            <Navigation className="w-3 h-3 text-green-600 flex-shrink-0" title="Following" />
                          )}
                        </div>
                        <span className={`font-arial text-[10px] truncate ${
                          friend.is_online ? 'text-[#666]' : 'text-[#999]'
                        }`}>
                          {friend.is_online 
                            ? (friend.current_room ? `In: ${friend.current_room.replace(/-/g, ' ')}` : friend.status_message || 'Available')
                            : (friend.last_seen ? `Offline · seen ${formatLastSeen(friend.last_seen)}` : 'Offline')}
                        </span>
                      </div>
                    </div>
                    
                    {/* Action Dropdown Button */}
                    <div className="relative">
                      <button 
                        className="p-1 hover:bg-[#D0D0D0] rounded"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowFollowMenu(showFollowMenu === friend.user_id ? null : friend.user_id);
                        }}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      
                      {/* Dropdown Menu */}
                      {showFollowMenu === friend.user_id && (
                        <div className="absolute right-0 top-full mt-1 bg-white retro-window z-50 min-w-[140px] shadow-lg">
                          <div className="p-1 bg-[#C0C0C0]">
                            <button 
                              className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                onStartPM?.(friend);
                                setShowFollowMenu(null);
                              }}
                            >
                              💬 Message
                            </button>
                            <button 
                              className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/profile/${friend.user_id}`);
                                setShowFollowMenu(null);
                              }}
                            >
                              👤 View Profile
                            </button>
                            <div className="border-t border-[#808080] my-1" />
                            {followingUser?.user_id === friend.user_id ? (
                              <button 
                                className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2 text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStopFollow();
                                  setShowFollowMenu(null);
                                }}
                              >
                                🚫 Stop Following
                              </button>
                            ) : (
                              <button 
                                className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2 text-green-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFollow(friend);
                                }}
                                data-testid={`follow-${friend.user_id}`}
                              >
                                📍 Follow{!friend.is_online ? " (offline)" : ""}
                              </button>
                            )}
                            <div className="border-t border-[#808080] my-1" />
                            <button 
                              className="w-full text-left px-2 py-1 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2 text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFriend(friend.user_id);
                                setShowFollowMenu(null);
                              }}
                            >
                              🗑️ Remove Friend
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === "requests" && (
            <div className="p-1">
              {friendRequests.length === 0 ? (
                <p className="text-xs text-center text-[#808080] p-4">No pending requests</p>
              ) : (
                friendRequests.map((request) => (
                  <div 
                    key={request.user_id} 
                    className="flex items-center justify-between p-1 border-b border-[#E0E0E0]"
                  >
                    <span className="font-arial text-xs">{request.username}</span>
                    <div className="flex gap-1">
                      <button 
                        className="retro-button p-1"
                        onClick={() => acceptRequest(request.user_id)}
                        data-testid={`accept-${request.user_id}`}
                      >
                        <Check className="w-3 h-3 text-green-600" />
                      </button>
                      <button 
                        className="retro-button p-1"
                        onClick={() => rejectRequest(request.user_id)}
                        data-testid={`reject-${request.user_id}`}
                      >
                        <XIcon className="w-3 h-3 text-red-600" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === "search" && (
            <div className="p-1">
              <div className="flex gap-1 mb-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="retro-input flex-1 text-xs"
                  placeholder="Search users..."
                  data-testid="search-users-input"
                />
                <button className="retro-button p-1">
                  <Search className="w-3 h-3" />
                </button>
              </div>

              {searchResults.length === 0 && searchQuery && (
                <p className="text-xs text-center text-[#808080] p-4">No users found</p>
              )}

              {searchResults.map((result) => (
                <div 
                  key={result.user_id} 
                  className="flex items-center justify-between p-1 hover:bg-[#E0E0E0]"
                >
                  <span className="font-arial text-xs">{result.username}</span>
                  <button 
                    className="retro-button p-1"
                    onClick={() => sendFriendRequest(result.user_id)}
                    data-testid={`add-friend-${result.user_id}`}
                  >
                    <UserPlus className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* User Action Menu */}
      {menuUser && (
        <UserActionMenu
          targetUser={menuUser}
          position={menuPosition}
          onClose={() => setMenuUser(null)}
          onStartPM={(user) => {
            onStartPM?.(user);
            setMenuUser(null);
          }}
          onRequestWebcam={(user) => {
            onRequestWebcam?.(user);
            setMenuUser(null);
          }}
          onViewProfile={(user) => {
            onViewProfile?.(user);
            setMenuUser(null);
          }}
        />
      )}
    </div>
  );
};

export default FriendsPanel;
